import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { ScrollArea } from "./ui/scroll-area";
import {
  Ruler,
  Triangle,
  Save,
  Download,
  Trash2,
  Activity,
  Square,
  RefreshCw,
  RotateCcw,
  Folder,
  InfoIcon,
} from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";

import SaveMeasurementModal from "./SaveMeasurementModal";
import Calculator from "./ui/Calculator";
import CustomPrimaryButton from "../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import { Checkbox, Input, message, Select } from "antd";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { dentalIcons } from "../../dentail-indetifcation/utils-icons/dentalIcons";
import CustomSelect from "./ui/CustomSelect";
import CurvesPreview from "./ui/CurvesPreview";
import CustomButtonWithIcon from "../../dentail-indetifcation/components/d-reusable/CustomButtonWithIcon";
import {
  _teethOptions,
  cameriereTeethList,
  formateDateAndTime,
  toTitleCase,
  transformSaveMeasurmentsForAgeEstimation,
} from "../../helper";
import { onSubmitAiCamererieData } from "../../components/https/ai/ai";
import { useDispatch, useSelector } from "react-redux";
import { _authenticateUser } from "../../redux/actions/authAction";
import CustomModal from "../../age-assessment/components/modals/CustomModal";
import ThreeThumbSlider from "./ui/ThreeThumbSlider";
import { fetchAnalysisDoc } from "../helpers/normalize/normalize";
import { getAllAnalysisProjects } from "../../components/https/image-analysis-apis";
import PolygonsShimer from "./shimer/PolygonsShimer";
import ToothBadge from "./ToothBadge";
import { diffPolygonMetrics } from "../helpers/polygonMetrics";
import PolygonComparisonMetricsCard from "./PolygonComparisonMetricsCard";

export function MeasurementPanel({
  projectId,
  scale,
  measurements,
  savedMeasurements,
  onSaveMeasurement,
  onDeleteSavedMeasurement,
  onRemoveActiveMeasurement,
  showCalculator,
  setShowCalculator,
  toggleInvert,
  toggleSharpen,
  showCommentPanel,
  CommentPanel,
  imageControls,
  onContrastChange,
  onBrightnessChange,
  resetImageProps,
  curves,
  onCurvesShadowsChange,
  onCurvesMidtonesChange,
  onCurvesHighlightsChange,
  onCurvesReset,
  resetSmartSharpen,
  onReduceNoiseChange,
  onSharpenRadiusChange,
  onSharpenAmountChange,
  sharpenAmount,
  sharpenNoise,
  sharpenRadius,
  levelsAdjustments,
  onLevelAdjustment,
  onLevelsReset,
  levelsHistogram,
  onAppendPolygon,
  onClearCamparedResult,
  onHighlightPolygon,
  onTabChange,
  onUpdateSavedMeasurement,
}) {
  const dispatch = useDispatch();

  const { contrast, brightness, zoom } = imageControls;

  const [selectedTab, setSelectedTab] = useState(0);
  const [activeTab, setActiveTab] = useState("active");
  const [showEstimateAge, setShowEstimateAge] = useState(false);
  const [showEstimateAgeResult, setShowEstimateAgeResult] = useState(false);
  const [showImportPolygonWindow, setShowImportPolygonWindow] = useState(false);
  const [selectedId, setSelectdId] = useState(null);
  const [selectedPolygonId, setSelectdPolygonId] = useState(null);
  const [selectedPolygons, setSelectedPolygons] = useState([]);
  const [isPolygonImported, setIsPolygonImported] = useState(false);
  const [query, setQuery] = useState("");
  const [polygonsLoading, setPolygonsLoading] = useState(false);

  // Per-item input state
  const [toothById, setToothById] = useState({}); // { [id]: '12' }
  const [labelById, setLabelById] = useState({}); // { [id]: 'Tooth' | 'Pulp' }
  const [toothErrById, setToothErrById] = useState({}); // { [id]: true | false }
  const [labelErrById, setLabelErrById] = useState({}); // { [id]: true | false }

  const [loading, setLoading] = useState(false);
  const [aiResponseData, setAiResponseData] = useState([]);
  const [selectedPolygonForCamparison, setSelectedPolygonForCamparison] =
    useState(null);

  const [
    selectedMeasuremntForAgeEstimation,
    setSelectedMeasuremntForAgeEstimation,
  ] = useState([]);
  const [measurementsForComparison, setMeasurementsForComparison] = useState(
    []
  );

  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const [projectList, setProjectList] = useState([]);
  const [basicAdjustmentOpen, setBasicAdjustmentOpen] = useState(true);
  const [exposureBalanceOpen, setExposureBalanceOpen] = useState(false);
  const [contrastCurveOpen, setContrastCurveOpen] = useState(false);
  const [clarityFilterOpen, setClarityFilterOpen] = useState(false);
  const [showComparisonMetricsDetails, setComparisonMetricsDetails] =
    useState(false);

  const u = scale > 0 ? "mm" : "px"; // unit for distance
  const u2 = scale > 0 ? "mm²" : "px"; // unit for area
  const perimeterUnit = scale > 0 ? "mm" : "px"; // perimeter is length

  function formatValue(measurement, type) {
    if (type === "linear") {
      return `${measurement.distance.toFixed(2)} ${u}`;
    }
    if (type === "angle") {
      return `${measurement.angle.toFixed(1)}°`;
    }
    if (type === "area") {
      return `A: ${measurement.area.toFixed(
        2
      )} ${u2}  (P: ${measurement.perimeter.toFixed(2)} ${perimeterUnit})`;
    }
    return "";
  }

  // 🔵 Direct save (no modal)

  // props/state you already have:
  // - saveMeasurements: Array of previously saved { id, type, value, toothNumber, label, ... }
  // - toothById, labelById, setToothErrById, setLabelErrById
  // - onSaveMeasurement, onRemoveActiveMeasurement
  // - formatValue(measurement, type)

  const toCamelCase = (str) =>
    str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase());

  function saveMeasurementDirect(measurement, type) {
    const id = measurement.id;
    const toothNum = (toothById[id] ?? "").trim();
    const toothTitle = teethOptions.find(
      (tooth) => tooth.fdi === toothById[id]
    )?.toothName;

    const labelVal = (labelById[id] ?? "").trim().toLowerCase(); // e.g. "tooth" | "pulp" | ""

    // Basic required validations
    if (!toothNum) {
      setToothErrById((prev) => ({ ...prev, [id]: true }));
      // optional: toast/error UI
      // message.error("Please select a tooth.");
      return;
    }
    if (type === "area" && !labelVal) {
      setLabelErrById((prev) => ({ ...prev, [id]: true }));
      // optional: toast/error UI
      // message.error("Please select a tooth.");
      return;
    }

    // ---- Uniqueness rules ----
    const exists = savedMeasurements || []; // your saved list

    if (type === "area") {
      const duplicatePair = exists.some(
        (m) =>
          m.type === "area" &&
          String(m.toothNumber) === String(toothNum) &&
          String((m.label ?? "").toLowerCase()) === labelVal
      );
      if (duplicatePair) {
        // Optional UI feedback:
        console.log("toothNum", toothNum);
        message.error(
          `Tooth ${toothTitle} -${toothNum} with label "${
            labelVal.charAt(0).toUpperCase() + labelVal.slice(1)
          }" is already saved. Choose a different label.`
        );
        return;
      }
    }

    // Passed validation -> save
    const savedMeasurement = {
      id: measurement.id,
      type,
      value: formatValue(measurement, type),
      toothNumber: toothNum,
      label: labelVal, // keep empty for non-polygon
      notes: "",
      timestamp: new Date().toISOString(),
      points: measurement?.points || [],
      arm1Distance: measurement?.arm1Distance ?? "",
      arm2Distance: measurement?.arm2Distance ?? "",
      color: measurement?.color || "#FFD700",
    };

    onSaveMeasurement?.(savedMeasurement);
    onRemoveActiveMeasurement?.(measurement.id);

    // clear errors on success
    setToothErrById((prev) => ({ ...prev, [id]: false }));
  }

  const linearMeasurements = measurements.filter(
    (m) => "start" in m && "end" in m
  );
  const angleMeasurements = measurements.filter(
    (m) => "center" in m && "point1" in m && "point2" in m
  );
  const areaMeasurements = measurements.filter(
    (m) => "points" in m && "area" in m
  );

  // Sort saved measurements with latest first
  const sortedSavedMeasurements = [...savedMeasurements].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const saveLinearHistory = sortedSavedMeasurements.filter(
    (el) => el.type === "linear"
  );
  const saveAngleHistory = sortedSavedMeasurements.filter(
    (el) => el.type === "angle"
  );
  const saveAreaHistory = sortedSavedMeasurements.filter(
    (el) => el.type === "area"
  );

  const teethOptions = cameriereTeethList;

  const measurmentsForAgeEstimation = (savedMeasurements || []).filter(
    (m) => m.type === "area"
  );

  const handleSubmitRecord = async () => {
    // Map selected rows -> API shape
    const teethPayload = (selectedMeasuremntForAgeEstimation || [])
      .map(({ fdi, pulpArea, toothArea }) => ({
        fdi: String(fdi),
        pulp_area: Number.isFinite(pulpArea) ? pulpArea : 0,
        tooth_area: Number.isFinite(toothArea) ? toothArea : 0,
      }))
      // optional: keep only teeth that have at least one area > 0
      .filter((t) => t.pulp_area > 0 || t.tooth_area > 0);

    const payload = {
      gender: "",
      teeth: teethPayload,
    };

    // 3) Submit
    await submitCameriereAiData(payload);
  };

  const submitCameriereAiData = async (payload) => {
    try {
      setLoading(true);
      const response = await onSubmitAiCamererieData({ payload: payload });
      if (response.status === 200) {
        setAiResponseData(response.data.data);
        setLoading(false);
        setTimeout(() => {
          setShowEstimateAge(false);
          setShowEstimateAgeResult(true);
        }, 200);
      }

      setLoading(false);
    } catch (error) {
      if (error?.response?.status === 401 || error?.code === "ERR_NETWORK") {
        localStorage.removeItem("user");
        dispatch(_authenticateUser(false));
        setLoading(false);
      }
      message.error(error.response.data.message);
      setLoading(false);
    }
  };

  const handleSelectMeasurmentForEstimation = (item) => {
    setSelectedMeasuremntForAgeEstimation((prev) => {
      // keep only objects (cleans up any accidental strings)
      const clean = prev.filter((v) => typeof v === "object");
      const exists = clean.some((x) => x.fdi === item.fdi);
      return exists
        ? clean.filter((x) => x.fdi !== item.fdi) // remove
        : [...clean, item]; // add object
    });
  };

  const trnsformMeasuremnts = transformSaveMeasurmentsForAgeEstimation(
    measurmentsForAgeEstimation,
    teethOptions
  );

  // Suppose you want to toggle polygonId
  const handleTogglePolygon = (polygonId) => {
    setSelectedPolygons((prev) =>
      prev.includes(polygonId) ? [] : [polygonId]
    );
  };

  useEffect(() => {
    getAllProjects();
  }, [query]);

  const getAllProjects = async () => {
    try {
      const response = await getAllAnalysisProjects({ search: query });
      if (response.status === 200) {
        setProjectList(response.data.data);
      }
    } catch (error) {
      console.log("eror", error);
    }
  };

  const handleSelectProject = async (item) => {
    try {
      const id = item?.id ?? null;
      setSelectedProjectId(id);
      setPolygonsLoading(true);
      const jsonUrl = item?.analysis_data;
      if (!jsonUrl) {
        setMeasurementsForComparison([]);
        setPolygonsLoading(false);

        return;
      }

      const doc = await fetchAnalysisDoc(jsonUrl);
      if (!doc) {
        setMeasurementsForComparison([]);
        setPolygonsLoading(false);

        return;
      }

      // Prefer overlays.savedMeasurements if it exists AND is an array.
      // (Also gracefully handle cases where data may live at doc.savedMeasurements.)
      const saved = Array.isArray(doc?.overlays?.savedMeasurements)
        ? doc.overlays.savedMeasurements
        : Array.isArray(doc?.savedMeasurements)
        ? doc.savedMeasurements
        : [];

      setMeasurementsForComparison(saved);

      if (saved.length === 0) {
        setPolygonsLoading(false);
      }
      setPolygonsLoading(false);
    } catch (error) {
      setMeasurementsForComparison([]);
      setPolygonsLoading(false);
    }
  };

  const onImportPolygon = () => {
    onClearCamparedResult();
    const obj = selectedPolygons[0];
    onAppendPolygon(obj);
    setShowImportPolygonWindow(false);
    setIsPolygonImported(true);
  };

  const currentPolygonsOnCanvasForComparison =
    savedMeasurements.filter(
      (currentPolygon) => currentPolygon.type === "area"
    ) || [];

  // Find the polygon object that matches the selectedPolygonForCamparison id
  const comparisonTarget =
    currentPolygonsOnCanvasForComparison.find(
      (p) => p.id === selectedPolygonForCamparison?.id
    ) || null;

  const comparesionDiffernces = diffPolygonMetrics(
    selectedPolygons,
    comparisonTarget
  );
  const metrics = comparesionDiffernces?.metrics;
  const decision = comparesionDiffernces?.decision;

  console.log("comparesionDiffernces", comparesionDiffernces);
  // above your return (or just inline):
  // Parse "A: 317832.94 px  (P: 892.20 px)"
  const metricsMatch = (comparisonTarget?.value || "").match(
    /A:\s*([\d.,]+)\s*([^\s]+).*P:\s*([\d.,]+)\s*([^\s)]+)/
  );

  const areaValue = metricsMatch?.[1];
  const areaUnit = metricsMatch?.[2];
  const periValue = metricsMatch?.[3];
  const periUnit = metricsMatch?.[4];

  const handleClearCamparedResult = () => {
    setSelectdPolygonId(null);
    setSelectedPolygons([]);
    setIsPolygonImported(false);
    setSelectedPolygonForCamparison(null);
    onClearCamparedResult();
    onHighlightPolygon?.(null);
  };

  const [editingSaveItemId, setEditingSaveItemId] = useState(null);
  const [editingSaveItemLabel, setEditingSaveItemLabel] = useState(""); // Linear: holds toothNumber

  const beginEditSavedLinear = (row) => {
    setEditingSaveItemId(row.id);
    setEditingSaveItemLabel(String(row.toothNumber ?? "") || "");
  };

  const cancelEditSaved = () => {
    setEditingSaveItemId(null);
    setEditingSaveItemLabel("");
  };

  const saveEditSavedLinear = () => {
    if (!editingSaveItemLabel) {
      message.error("Please select a tooth");
      return;
    }
    // parent patch: update toothNumber only
    if (typeof onUpdateSavedMeasurement === "function") {
      onUpdateSavedMeasurement(editingSaveItemId, {
        toothNumber: editingSaveItemLabel,
      });
    }
    cancelEditSaved();
  };

  return (
    <div className="w-[100%] p-2 bg-white flex flex-col shadow-sm  min-h-full">
      {showCalculator ? (
        <Calculator setShowCalculator={setShowCalculator} />
      ) : showCommentPanel ? (
        <>{CommentPanel}</>
      ) : showEstimateAge ? (
        <div className="p-1">
          <div className="h-[80vh]">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#717784] font-semibold font-semibold text-[16px]">
                <img
                  src={dentalIcons.estimateAge}
                  className="h-6 w-6 icon-black"
                />
                Verify Age
              </div>
              <div
                onClick={() => setShowEstimateAge(false)}
                className="cursor-pointer"
              >
                <img src={dentalIcons.backCalculator} className="h-6 w-6" />
              </div>
            </div>
            {trnsformMeasuremnts.length > 0 && (
              <div className="space-y-1 mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-md text-gray-900">
                    Select teeth for age verification
                  </span>
                </div>
                {trnsformMeasuremnts.map((saved) => {
                  const isDisbaled =
                    saved.pulpArea === 0 || saved.toothArea === 0;
                  return (
                    <div
                      className="bg-[#F5F7FA] p-1 rounded-[10px]"
                      key={saved.id}
                    >
                      <div
                        className={`bg-[#FFFFFF] rounded-[10px] p-2 ${
                          isDisbaled
                            ? "bg-gray-100 opacity-50 cursor-not-allowed"
                            : ""
                        }  hover:bg-gray-100 transition-colors`}
                      >
                        <div className="flex flex-col items-start justify-between">
                          <div className="flex items-center gap-2 justify-between w-full">
                            <div className="flex items-center gap-2">
                              <div className="h-3 w-3 bg-[#0A9FD4]" />
                              <span className="font-medium text-[16px] text-[#535353]">
                                {saved.toothName} ({saved.fdi})
                              </span>
                            </div>
                            <Checkbox
                              disabled={isDisbaled}
                              checked={selectedMeasuremntForAgeEstimation.some(
                                (x) => x.fdi === saved.fdi
                              )}
                              onChange={() =>
                                handleSelectMeasurmentForEstimation(saved)
                              }
                            />
                            {/* <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDeleteSavedMeasurement(saved.id)}
                              className="h-4 w-4 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button> */}
                          </div>
                          <div className="flex w-full flex-col mt-2">
                            <div className="flex flex items-center justify-between w-full text-[#8C8C8C]">
                              <span className="text-[14px] font-semibold ">
                                Tooth Area
                              </span>

                              <span className="text-[15px] font-semibold ">
                                Pulp Area
                              </span>
                            </div>
                            <div className="flex flex items-center justify-between w-full">
                              <span className="text-[14px] font-semibold text-[#000000]">
                                {saved.toothArea > 0 ? saved.toothArea : "--"}{" "}
                                {saved.toothArea > 0 && saved.unit}
                              </span>

                              <span className="text-[15px] font-semibold text-[#000000]">
                                {saved.pulpArea > 0 ? saved.pulpArea : "--"}{" "}
                                {saved.pulpArea > 0 && saved.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <CustomPrimaryButton
              title={`Verify Age${
                selectedMeasuremntForAgeEstimation.length
                  ? ` (${selectedMeasuremntForAgeEstimation.length})`
                  : ""
              }`}
              onClick={handleSubmitRecord}
              isLoading={loading}
              leadingIcon={true}
              icon={dentalIcons.estimateAge}
              width={"100%"}
              isRound={true}
              isDisbale={selectedMeasuremntForAgeEstimation.length === 0}
            />
          </div>
        </div>
      ) : showEstimateAgeResult ? (
        <div className="p-1">
          <div className="h-[80vh]">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#717784] font-semibold font-semibold text-[16px]">
                <img
                  src={dentalIcons.estimateAge}
                  className="h-6 w-6 icon-black"
                />
                Verify Age
              </div>
              <div
                onClick={() => {
                  setShowEstimateAgeResult(false);
                  setShowEstimateAge(true);
                }}
                className="cursor-pointer"
              >
                <img src={dentalIcons.backCalculator} className="h-6 w-6" />
              </div>
            </div>
            {aiResponseData.length > 0 && (
              <div className="space-y-1 mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-md text-gray-900">
                    Results
                  </span>
                </div>
                {aiResponseData.map((saved) => {
                  return (
                    <div
                      className="bg-[#F5F7FA] p-1 rounded-[10px]"
                      key={saved.id}
                    >
                      <div
                        className={`bg-[#FFFFFF] rounded-[10px] p-2 hover:bg-gray-100 transition-colors`}
                      >
                        <div className="flex flex-col items-start justify-between">
                          <div className="flex items-center gap-2 justify-between w-full">
                            <div className="flex items-center gap-2">
                              <div className="h-3 w-3 bg-[#0A9FD4]" />
                              <span className="font-medium text-[16px] text-[#535353]">
                                {
                                  teethOptions.find(
                                    (tooth) => tooth.fdi === saved.fdi
                                  )?.toothName
                                }{" "}
                                ({saved.fdi})
                              </span>
                            </div>
                          </div>
                          <div className="flex w-full flex-col mt-2">
                            <div className="flex flex items-center justify-between w-full ">
                              <span className="text-[14px] font-semibold text-[#5B5E65]">
                                Dental Age
                              </span>

                              <span className="text-[15px] font-semibold text-[#0A9FD4]">
                                {saved?.dental_age}
                              </span>
                            </div>
                            <div className="flex flex items-center justify-between w-full text-[#8C8C8C]">
                              <span className="text-[14px] font-semibold text-[#5B5E65]">
                                Mean
                              </span>

                              <span className="text-[15px] font-semibold  text-[#000000] ">
                                {saved?.mean}
                              </span>
                            </div>
                            <div className="flex flex items-center justify-between w-full">
                              <span className="text-[14px] font-semibold text-[#5B5E65]">
                                Deviation
                              </span>

                              <span className="flex items-center gap-1 text-[15px] font-semibold text-[#000000]">
                                <img
                                  src={dentalIcons.plusMinus}
                                  style={{ height: 10 }}
                                />
                                {saved.standard_deviation || "N/A"}
                              </span>
                            </div>
                            <div className="flex flex items-center justify-between w-full">
                              <span className="text-[14px] font-semibold text-[#5B5E65]">
                                Range
                              </span>

                              <span className="text-[15px] font-semibold text-[#000000]">
                                {saved.range}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : isPolygonImported ? (
        <div className="p-1 image_analysis_container">
          <div className="min-h-[80vh] max-h-full">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#717784] font-semibold font-semibold text-[16px]">
                <img src={dentalIcons.polygonIcon} className="h-6 w-6" />
                Morphometric Analysis
              </div>
              <div
                onClick={() => {
                  handleClearCamparedResult();
                  setIsPolygonImported(false);
                }}
                className="cursor-pointer"
              >
                <img src={dentalIcons.backCalculator} className="h-6 w-6" />
              </div>
            </div>
            <div className="w-full">
              {isPolygonImported ? (
                <div className="flex-1 w-full pt-1 space-y-3">
                  <div className="flex items-center justify-between w-full">
                    <p className="font-semibold text-black text-[15px]">
                      Reference Shape
                    </p>
                    {selectedPolygons.length > 0 &&
                      !selectedPolygonForCamparison && (
                        <div
                          onClick={() => setShowImportPolygonWindow(true)}
                          className="flex items-center gap-1.5 text-[#0A9FD4] cursor-pointer transition-all duration-150 hover:text-[#0879A5] hover:underline"
                        >
                          <RefreshCw className="w-4 h-4" />
                          <span className="text-sm font-medium select-none">
                            Replace
                          </span>
                        </div>
                      )}
                  </div>

                  {selectedPolygons.map((el, index) => {
                    const m = (el.value || "").match(
                      /A:\s*([\d.]+)\s*([^\s]+).*P:\s*([\d.]+)\s*([^\s)]+)/
                    );
                    return (
                      <div
                        className="w-full p-2 rounded-[8px] border-2 border-[#10B981] flex flex-col"
                        key={index}
                      >
                        <div className="flex flex-col items-start justify-between w-full">
                          <ToothBadge saved={el} />

                          <div className="flex items-center gap-2">
                            {/* Color indicator */}
                            <span
                              className="inline-block w-3 h-3 rounded-full"
                              style={{ backgroundColor: "#10B981" }}
                            ></span>
                            <span className="font-bold text-[14px] text-[#535353]">
                              {
                                teethOptions.find(
                                  (t) => t.fdi === el.toothNumber
                                )?.toothName
                              }{" "}
                              ({el.toothNumber})
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 pb-1 mt-2">
                          <div className="bg-[#FFFFFF] rounded-full border border-[#E8E9EB] px-2 py-1 flex items-center gap-2 text-[#5B5E65] font-medium ">
                            {/* <img src={dentalIcons.polyArea} alt="area" /> */}
                            <p>
                              A:{" "}
                              <span className="font-mono">
                                {m[1]} {m[2]}
                              </span>
                            </p>
                          </div>
                          <div className="bg-[#FFFFFF] rounded-full border border-[#E8E9EB] px-2 py-1 flex items-center gap-2 text-[#5B5E65] font-medium">
                            {/* <img
                              src={dentalIcons.polyPrimter}
                              alt="perimeter"
                            /> */}
                            <p>
                              P:{" "}
                              <span className="font-mono">
                                {m[3]} {m[4]}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="w-full">
                    <p className="font-semibold text-black text-[14px]">
                      Select Tooth{" "}
                    </p>
                    <div className="flex items-center gap-2">
                      <Select
                        className="select-compact w-full"
                        placeholder="Select a Polygon"
                        allowClear
                        optionFilterProp="label" // search by the same label text
                        optionLabelProp="label" // 👈 show this when selected (not children)
                        onChange={(val) => {
                          const selectedObject =
                            currentPolygonsOnCanvasForComparison?.find(
                              (item) => item.id === val
                            );
                          onHighlightPolygon?.(val);
                          setSelectedPolygonForCamparison(selectedObject);
                        }}
                      >
                        {currentPolygonsOnCanvasForComparison?.map((poly) => {
                          const toothTitle =
                            teethOptions.find((t) => t.fdi === poly.toothNumber)
                              ?.toothName || "tooth";
                          const labelText = `${toothTitle} (${poly.toothNumber})`;

                          return (
                            <Select.Option
                              key={poly.id}
                              value={poly.id}
                              label={labelText} // 👈 this is what appears when selected
                            >
                              {/* Rich content only in the dropdown menu */}
                              <div className="flex items-center justify-between w-full">
                                <span>{labelText}</span>
                                <ToothBadge saved={poly} />
                              </div>
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </div>
                  </div>

                  {comparisonTarget && (
                    <p className="font-semibold text-black text-[14px]">
                      Compared Shape
                    </p>
                  )}
                  {comparisonTarget && (
                    <React.Fragment>
                      <div
                        className="p-2 bg-[#F5F7FA] rounded-[8px] w-full border-2 border-[#ED585A]"
                        key={comparisonTarget?.id}
                      >
                        <div className="flex flex-col items-start justify-between w-full">
                          <ToothBadge saved={comparisonTarget} />

                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-3 h-3 rounded-full"
                              style={{ backgroundColor: "#ED585A" }}
                            ></span>
                            <span className="font-bold text-[14px] text-[#535353]">
                              {
                                teethOptions.find(
                                  (t) => t.fdi === comparisonTarget?.toothNumber
                                )?.toothName
                              }{" "}
                              ({comparisonTarget?.toothNumber})
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 pb-1 mt-2">
                          <div className="bg-[#FFFFFF] rounded-full border border-[#E8E9EB] px-2 py-1 flex items-center gap-2 text-[#5B5E65] font-medium ">
                            {/* <img src={dentalIcons.polyArea} alt="area" /> */}
                            <p>
                              A:{" "}
                              <span className="font-mono">
                                {areaValue} {areaUnit}
                              </span>
                            </p>
                          </div>
                          <div className="bg-[#FFFFFF] rounded-full border border-[#E8E9EB] px-2 py-1 flex items-center gap-2 text-[#5B5E65] font-medium">
                            {/* <img
                              src={dentalIcons.polyPrimter}
                              alt="perimeter"
                            /> */}
                            <p>
                              P:{" "}
                              <span className="font-mono">
                                {periValue} {periUnit}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <hr />
                      <p className="font-semibold text-black text-[14px]">
                        Comparison Results
                      </p>
                      {comparesionDiffernces?.isValid ? (
                        <PolygonComparisonMetricsCard
                          metrics={metrics}
                          decision={decision}
                          decisionIcon={dentalIcons.decision}
                        />
                      ) : (
                        <div className="bg-[#F5F7FA] p-2 rounded-[8px] text-[#535353]">
                          {comparesionDiffernces?.reason}
                        </div>
                      )}

                      <div
                        onClick={handleClearCamparedResult}
                        className="group flex items-center justify-center gap-2 cursor-pointer text-[#0A9FD4] font-medium hover:text-[#0879A5] transition-all duration-150"
                      >
                        <RotateCcw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
                        <span className="select-none group-hover:underline">
                          Clear Results
                        </span>
                      </div>
                    </React.Fragment>
                  )}
                </div>
              ) : (
                <div className="p-2 bg-[#DFF3FA] border border-[#E2F7FF] rounded-[12px]">
                  <div className="p-3 bg-white  rounded-[12px]">
                    <p className="mb-2">
                      Compare polygons from previous radiographs with those in
                      the current image for morphological and dimensional
                      analysis.
                    </p>
                    <CustomPrimaryButton
                      icon={dentalIcons.importPoly}
                      title="Import Polygon"
                      isRound={true}
                      leadingIcon
                      width={"100%"}
                      onClick={() => setShowImportPolygonWindow(true)}
                    />
                    <small>
                      Import polygons from other projects to perform comparative
                      analysis.
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="relative bg-white sticky top-0 z-[99]">
            <div className="bg-[#F5F7FA] p-1 rounded-[8px] flex gap-2 w-[100%]">
              <div
                onClick={() => {
                  setSelectedTab(0);
                  onTabChange(0);
                }}
                className={`${
                  selectedTab === 0 ? "bg-white" : "br-transparent"
                } w-[30%]  rounded-[8px] p-[5px] cursor-pointer flex items-center justify-center text-[#0E121B]`}
                style={{
                  boxShadow:
                    selectedTab === 0 &&
                    "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
                }}
              >
                Enhance
              </div>
              <div
                onClick={() => {
                  setSelectedTab(1);
                  onTabChange(1);
                }}
                className={`${
                  selectedTab === 1 ? "bg-white" : "br-transparent"
                } w-[40%] rounded-[8px] p-[5px] cursor-pointer flex items-center justify-center text-[#0E121B]`}
                style={{
                  boxShadow:
                    selectedTab === 1 &&
                    "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
                }}
              >
                Measure
              </div>
              <div
                onClick={() => {
                  setSelectedTab(2);
                  onTabChange(2);
                }}
                className={`${
                  selectedTab === 2 ? "bg-white" : "br-transparent"
                } w-[30%]  rounded-[8px] p-[5px] cursor-pointer flex items-center justify-center text-[#0E121B]`}
                style={{
                  boxShadow:
                    selectedTab === 2 &&
                    "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
                }}
              >
                Analyze
              </div>
            </div>
          </div>
          {selectedTab === 0 && (
            <>
              {/* tools */}
              <div className="p-2 bg-[#F5F7FA] mt-3 rounded-[16px] image_analysis_container">
                <div className="flex items-center justify-between">
                  <div className="flex gap-2 items-center">
                    <p className="text-[#000000] font-semibold">
                      Basic Adjustment
                    </p>
                  </div>
                  <p
                    className="text-[#20C3FF] font-semibold cursor-pointer"
                    onClick={resetImageProps}
                  >
                    {" "}
                    Reset
                  </p>
                </div>
                {basicAdjustmentOpen && (
                  <div className="space-y-2 mt-3">
                    {/* Contrast */}

                    <div className="flex w-full items-center gap-2">
                      <Label className="text-sm w-[100px] font-medium text-[#535353] block">
                        Contrast
                      </Label>
                      <Slider
                        value={[contrast.value]}
                        onValueChange={([v]) => onContrastChange?.(v)}
                        min={contrast.min}
                        max={contrast.max}
                        step={contrast.step}
                        className="w-full"
                      />
                    </div>
                    {/* Brightness  */}
                    <div className="flex w-full items-center gap-2">
                      <Label className="text-sm w-[100px] font-medium text-[#535353] block">
                        Brightness
                      </Label>
                      <Slider
                        value={[brightness.value]}
                        onValueChange={([v]) => onBrightnessChange?.(v)}
                        min={brightness.min}
                        max={brightness.max}
                        step={brightness.step}
                        className="w-full"
                      />
                    </div>
                    {/* Sharpness  */}

                    <div
                      onClick={toggleSharpen}
                      className="bg-white rounded-[8px] border border-[#EFEFEF] flex items-center justify-center gap-2 h-[36px] text-[#323538] cursor-pointer mt-3"
                    >
                      <img src={dentalIcons.invertIcon} alt="invert" />
                      <span>Sharpness</span>
                    </div>
                    <div
                      onClick={toggleInvert}
                      className="bg-white rounded-[8px] border border-[#EFEFEF] flex items-center justify-center gap-2 h-[36px] text-[#323538] cursor-pointer"
                    >
                      <img src={dentalIcons.invertIcon} alt="invert" />
                      <span>Invert Image</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Tone Control */}
              <div className="p-2 bg-[#F5F7FA] mt-3 rounded-[16px]  image_analysis_container">
                <div className="flex items-center justify-between">
                  <div
                    className="flex gap-2 items-center"
                    onClick={() => setExposureBalanceOpen(!exposureBalanceOpen)}
                  >
                    <img
                      src={
                        exposureBalanceOpen
                          ? dentalIcons.openArrow
                          : dentalIcons.closeArrow
                      }
                      alt="arrow"
                      className="mt-1"
                    />
                    <p className="text-[#000000] font-semibold">
                      Exposure Balance
                    </p>
                  </div>
                  {exposureBalanceOpen && (
                    <p
                      className="text-[#20C3FF] font-semibold cursor-pointer"
                      onClick={onLevelsReset}
                    >
                      Reset
                    </p>
                  )}
                </div>
                {exposureBalanceOpen && (
                  <>
                    {/* histogram (inline SVG; recomputes when levelsHistogram changes) */}
                    <div className="h-[130px] mx-1 flex items-center justify-center rounded-[8px] border border-[#EFEFEF] bg-white mt-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="100%"
                        height="130"
                        viewBox="0 0 256 115"
                        preserveAspectRatio="none"
                      >
                        {(() => {
                          const max = Math.max(1, ...levelsHistogram);
                          const bars = levelsHistogram.map((v, i) => {
                            const h = Math.max(1, (v / max) * 110);
                            return (
                              <rect
                                key={i}
                                x={i}
                                y={115 - h}
                                width="1"
                                height={h}
                                fill="#CACACA"
                              />
                            );
                          });
                          const bx = (levelsAdjustments.black / 100) * 256;
                          const mx = (levelsAdjustments.mid / 100) * 256;
                          const wx = (levelsAdjustments.white / 100) * 256;
                          return (
                            <>
                              {bars}
                              <rect
                                x={bx}
                                y="0"
                                width={wx - bx}
                                height="115"
                                fill="rgba(32,195,255,0.08)"
                              />
                              <line
                                x1={bx}
                                y1="0"
                                x2={bx}
                                y2="115"
                                stroke="#20C3FF"
                                strokeWidth="0"
                              />
                              <line
                                x1={mx}
                                y1="0"
                                x2={mx}
                                y2="115"
                                stroke="#20C3FF"
                                strokeWidth="0"
                              />
                              <line
                                x1={wx}
                                y1="0"
                                x2={wx}
                                y2="115"
                                stroke="#20C3FF"
                                strokeWidth="0"
                              />
                            </>
                          );
                        })()}
                      </svg>
                    </div>

                    {/* triple-thumb slider */}
                    <div className="mt-2 px-1">
                      <ThreeThumbSlider
                        value={[
                          levelsAdjustments.black,
                          levelsAdjustments.mid,
                          levelsAdjustments.white,
                        ]}
                        onChange={onLevelAdjustment}
                        min={0}
                        max={100}
                        step={1}
                        className="w-full"
                      />
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="min-w-[48px] px-2 py-1 bg-white border border-[#EFEFEF] rounded-[5px] text-center font-medium">
                        {levelsAdjustments.black}
                      </div>
                      <div className="min-w-[48px] px-2 py-1 bg-white border border-[#EFEFEF] rounded-[5px] text-center font-medium">
                        {levelsAdjustments.mid}
                      </div>
                      <div className="min-w-[48px] px-2 py-1 bg-white border border-[#EFEFEF] rounded-[5px] text-center font-medium">
                        {levelsAdjustments.white}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Contrast Curve */}
              <div className="p-2 bg-[#F5F7FA] mt-3 rounded-[16px] image_analysis_container">
                <div className="flex items-center justify-between">
                  <div
                    className="flex gap-2 items-center"
                    onClick={() => setContrastCurveOpen(!contrastCurveOpen)}
                  >
                    <img
                      src={
                        contrastCurveOpen
                          ? dentalIcons.openArrow
                          : dentalIcons.closeArrow
                      }
                      alt="arrow"
                      className="mt-1"
                    />
                    <p className="text-[#000000] font-semibold">
                      Contrast Curve
                    </p>
                  </div>
                  {contrastCurveOpen && (
                    <p
                      className="text-[#20C3FF] font-semibold cursor-pointer"
                      onClick={onCurvesReset}
                    >
                      {" "}
                      Reset
                    </p>
                  )}
                </div>
                {contrastCurveOpen && (
                  <>
                    <div className="bg-white flex items-center justify-center rounded-[8px] border border-[#EFEFEF] mt-3">
                      <CurvesPreview
                        shadows={curves.shadows}
                        midtones={curves.midtones}
                        highlights={curves.highlights}
                        // width={130}
                        height={130}
                      />
                    </div>
                    <div className="space-y-2 mt-2">
                      {/* Shadows */}
                      <div className="flex w-full items-center gap-2">
                        <Label className="text-sm w-[100px] font-medium text-[#535353] block">
                          Shadows
                        </Label>
                        <Slider
                          value={[curves.shadows]}
                          onValueChange={([v]) => onCurvesShadowsChange(v)}
                          min={-1}
                          max={1}
                          step={0.05}
                          className="w-full"
                        />
                      </div>
                      {/* Midtones */}
                      <div className="flex w-full items-center gap-2">
                        <Label className="text-sm w-[100px] font-medium text-[#535353] block">
                          Midtones
                        </Label>
                        <Slider
                          value={[curves.midtones]}
                          onValueChange={([v]) => onCurvesMidtonesChange(v)}
                          min={-1}
                          max={1}
                          step={0.05}
                          className="w-full"
                        />
                      </div>
                      {/* Highlights */}
                      <div className="flex w-full items-center gap-2">
                        <Label className="text-sm w-[100px] font-medium text-[#535353] block">
                          Highlights
                        </Label>
                        <Slider
                          value={[curves.highlights]}
                          onValueChange={([v]) => onCurvesHighlightsChange(v)}
                          min={-1}
                          max={1}
                          step={0.05}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Clarity Filter */}
              <div className="p-2 bg-[#F5F7FA] mt-3 rounded-[16px] image_analysis_container">
                <div className="flex items-center justify-between">
                  <div
                    className="flex gap-2 items-center"
                    onClick={() => setClarityFilterOpen(!clarityFilterOpen)}
                  >
                    <img
                      src={
                        clarityFilterOpen
                          ? dentalIcons.openArrow
                          : dentalIcons.closeArrow
                      }
                      alt="arrow"
                      className="mt-1"
                    />
                    <p className="text-[#000000] font-semibold">
                      Clarity Filter
                    </p>
                  </div>
                  {clarityFilterOpen && (
                    <p
                      className="text-[#20C3FF] font-semibold cursor-pointer"
                      onClick={resetSmartSharpen}
                    >
                      {" "}
                      Reset
                    </p>
                  )}
                </div>
                {clarityFilterOpen && (
                  <div className="space-y-2 mt-3">
                    {/* Amount */}
                    <div className="flex w-full items-center gap-2">
                      <Label className="text-sm w-[100px] font-medium text-[#535353] block">
                        Amount
                      </Label>
                      <Slider
                        value={[sharpenAmount]}
                        onValueChange={([v]) => onSharpenAmountChange?.(v)}
                        min={0}
                        max={1}
                        step={0.05}
                        className="w-full"
                      />
                    </div>
                    {/* Radius */}
                    <div className="flex w-full items-center gap-2">
                      <Label className="text-sm w-[100px] font-medium text-[#535353] block">
                        Radius
                      </Label>
                      <Slider
                        value={[sharpenRadius]}
                        onValueChange={([v]) => onSharpenRadiusChange?.(v)}
                        min={0}
                        max={3}
                        step={0.1}
                        className="w-full"
                      />
                    </div>
                    {/* Reduce Noise  */}
                    <div className="flex w-full items-center gap-2">
                      <Label className="text-sm w-[100px] font-medium text-[#535353] block">
                        Reduce Noise 
                      </Label>
                      <Slider
                        value={[sharpenNoise]}
                        onValueChange={([v]) => onReduceNoiseChange?.(v)}
                        min={0}
                        max={1}
                        step={0.05}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {selectedTab === 1 && (
            <>
              <div className="flex items-center w-full relative sticky top-10 z-[99] bg-white">
                <div
                  className={`w-1/2 p-2 text-center cursor-pointer transition-all text-[12px] ${
                    activeTab === "active"
                      ? "border-b-2 border-[#20C3FF] font-semibold text-[#20C3FF]"
                      : "border-b-2 border-[#EAEFF6] text-gray-600"
                  }`}
                  onClick={() => setActiveTab("active")}
                >
                  Active Measurements
                </div>
                <div
                  className={`w-1/2 p-2 text-center cursor-pointer transition-all text-[12px] ${
                    activeTab === "history"
                      ? "border-b-2 border-[#20C3FF] font-semibold text-[#20C3FF]"
                      : "border-b-2 border-[#EAEFF6] text-gray-600"
                  }`}
                  onClick={() => setActiveTab("history")}
                >
                  Saved Measurements
                </div>
              </div>

              {/* Active Measurements */}
              {activeTab === "active" && (
                <div className="w-full px-1 py-2 border-b border-gray-100">
                  {measurements.length > 0 ? (
                    <div className="space-y-4">
                      {/* Linear Measurements */}
                      {linearMeasurements.filter(
                        (m) => !saveLinearHistory.some((s) => s.id === m.id)
                      ).length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <img src={dentalIcons.linear} />
                            <span className="font-semibold text-md text-gray-900">
                              Linear ({linearMeasurements.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {linearMeasurements
                              .filter(
                                (m) =>
                                  !saveLinearHistory.some((s) => s.id === m.id)
                              )
                              .map((measurement, index) => (
                                <React.Fragment key={index}>
                                  <div
                                    key={measurement.id}
                                    style={{
                                      borderColor:
                                        measurement?.color === "#FFFFFF"
                                          ? "#e2e5e8ff"
                                          : measurement?.color || "#00FF00",
                                    }}
                                    className={`bg-[#F5F7FA] p-[7px] rounded-[10px] flex items-center gap-1 border-[1.5px]  bg-[#E9F8FE]`}
                                  >
                                    <div className="w-full flex flex-col bg-white p-[5px] rounded-[10px] justify-between items-start">
                                      <div className="flex w-full items-center gap-2">
                                        <Select
                                          className="select-compact"
                                          style={{ width: "100%" }}
                                          placeholder="Select Tooth"
                                          value={
                                            toothById[measurement.id] ??
                                            undefined
                                          } // undefined => show placeholder
                                          status={
                                            toothErrById[measurement.id]
                                              ? "error"
                                              : undefined
                                          }
                                          optionFilterProp="children"
                                          allowClear
                                          onChange={(val) => {
                                            // val is the selected fdi (or undefined when cleared)
                                            setToothById((prev) => ({
                                              ...prev,
                                              [measurement.id]: val,
                                            }));

                                            const isValid =
                                              !!val &&
                                              String(val).trim().length > 0;
                                            setToothErrById((prev) => ({
                                              ...prev,
                                              [measurement.id]: !isValid,
                                            }));
                                          }}
                                        >
                                          {[
                                            "Upper Right Quadrant",
                                            "Upper Left Quadrant",
                                            "Lower Left Quadrant",
                                            "Lower Right Quadrant",
                                          ].map((quad) => (
                                            <Select.OptGroup
                                              key={quad}
                                              label={quad}
                                            >
                                              {teethOptions
                                                .filter(
                                                  (tooth) =>
                                                    tooth.quadrant === quad
                                                )
                                                .map((t) => (
                                                  <Select.Option
                                                    key={t.fdi}
                                                    value={t.fdi}
                                                  >
                                                    {t.toothName} -{t.fdi}
                                                  </Select.Option>
                                                ))}
                                            </Select.OptGroup>
                                          ))}
                                        </Select>
                                      </div>
                                      {toothErrById[measurement.id] && (
                                        <span className="text-[11px] text-red-500 mt-1">
                                          Tooth # required
                                        </span>
                                      )}
                                      <div className="w-full flex justify-start">
                                        <span className="p-1 text-sm text-[#525866] font-semibold">
                                          D = {measurement.distance.toFixed(2)}{" "}
                                          {scale > 0 ? "mm" : "px"}
                                        </span>
                                      </div>
                                    </div>

                                    <div
                                      className={`text-center cursor-pointer w-[50px] ${
                                        selectedId === measurement.id
                                          ? "text-[#0A9FD4]"
                                          : "text-[#525866]"
                                      } font-semibold`}
                                      onClick={() =>
                                        saveMeasurementDirect(
                                          measurement,
                                          "linear"
                                        )
                                      }
                                    >
                                      Save
                                    </div>
                                  </div>
                                </React.Fragment>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Angle Measurements */}
                      {angleMeasurements.filter(
                        (m) => !saveAngleHistory.some((s) => s.id === m.id)
                      ).length > 0 && (
                        <div className="space-y-3">
                          {linearMeasurements.length > 0 && <Separator />}
                          <div className="flex items-center gap-2">
                            <img src={dentalIcons.angles} />
                            <span className="font-semibold text-md text-gray-900">
                              Angles ({angleMeasurements.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {angleMeasurements
                              .filter(
                                (m) =>
                                  !saveAngleHistory.some((s) => s.id === m.id)
                              )
                              .map((measurement, index) => (
                                <React.Fragment key={index}>
                                  <div
                                    key={measurement.id}
                                    style={{
                                      borderColor:
                                        measurement?.color === "#FFFFFF"
                                          ? "#e2e5e8ff"
                                          : measurement?.color || "#E1E6EF",
                                    }}
                                    className={`bg-[#F5F7FA] p-[7px] rounded-[10px] flex items-center gap-1 
                                        border-[1.5px] bg-[#E9F8FE]
                                      
                                    }`}
                                  >
                                    <div className="w-full flex-col bg-white p-[5px] rounded-[10px] justify-between items-center">
                                      <div className="flex w-full items-center gap-2">
                                        <Select
                                          className="select-compact"
                                          style={{ width: "100%" }}
                                          placeholder="Select Tooth"
                                          value={
                                            toothById[measurement.id] ??
                                            undefined
                                          } // undefined => show placeholder
                                          status={
                                            toothErrById[measurement.id]
                                              ? "error"
                                              : undefined
                                          }
                                          optionFilterProp="children"
                                          allowClear
                                          onChange={(val) => {
                                            // val is the selected fdi (or undefined when cleared)
                                            setToothById((prev) => ({
                                              ...prev,
                                              [measurement.id]: val,
                                            }));

                                            const isValid =
                                              !!val &&
                                              String(val).trim().length > 0;
                                            setToothErrById((prev) => ({
                                              ...prev,
                                              [measurement.id]: !isValid,
                                            }));
                                          }}
                                        >
                                          {[
                                            "Upper Right Quadrant",
                                            "Upper Left Quadrant",
                                            "Lower Left Quadrant",
                                            "Lower Right Quadrant",
                                          ].map((quad) => (
                                            <Select.OptGroup
                                              key={quad}
                                              label={quad}
                                            >
                                              {teethOptions
                                                .filter(
                                                  (tooth) =>
                                                    tooth.quadrant === quad
                                                )
                                                .map((t) => (
                                                  <Select.Option
                                                    key={t.fdi}
                                                    value={t.fdi}
                                                  >
                                                    {t.toothName} - {t.fdi}
                                                  </Select.Option>
                                                ))}
                                            </Select.OptGroup>
                                          ))}
                                        </Select>
                                      </div>
                                      {toothErrById[measurement.id] && (
                                        <span className="text-[11px] text-red-500">
                                          Tooth # required
                                        </span>
                                      )}
                                      <div className="flex w-full items-center">
                                        <div className="w-full flex flex-col">
                                          <span className="text-sm text-[#525866] font-semibold">
                                            Angle ={" "}
                                            {measurement.angle.toFixed(1)}°
                                          </span>
                                          <span className="text-sm text-[#525866] font-semibold">
                                            D1={" "}
                                            {measurement.arm1Distance.toFixed(
                                              1
                                            )}{" "}
                                            {scale > 0 ? "mm" : "px"}
                                          </span>
                                          <span className="text-sm text-[#525866] font-semibold">
                                            D2={" "}
                                            {measurement.arm2Distance.toFixed(
                                              1
                                            )}{" "}
                                            {scale > 0 ? "mm" : "px"}
                                          </span>
                                        </div>

                                        {/* <div className="flex justify-end">
                                          <span className="text-sm text-[#525866] font-semibold">
                                            {measurement.angle.toFixed(1)}°
                                          </span>
                                        </div> */}
                                      </div>
                                    </div>
                                    <div
                                      className={`text-center cursor-pointer w-[50px] ${
                                        selectedId === measurement.id
                                          ? "text-[#0A9FD4]"
                                          : "text-[#525866]"
                                      } font-semibold`}
                                      onClick={() =>
                                        saveMeasurementDirect(
                                          measurement,
                                          "angle"
                                        )
                                      }
                                    >
                                      Save
                                    </div>
                                  </div>
                                </React.Fragment>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Area Measurements */}
                      {areaMeasurements.filter(
                        (m) => !saveAreaHistory.some((s) => s.id === m.id)
                      ).length > 0 && (
                        <div className="space-y-3">
                          {(linearMeasurements.length > 0 ||
                            angleMeasurements.length > 0) && <Separator />}
                          {areaMeasurements.filter(
                            (m) =>
                              !saveAreaHistory.some((s) => s.id === m.id) &&
                              m.isImport === false
                          ).length > 0 && (
                            <div className="flex items-center gap-2">
                              <img src={dentalIcons.polygons} />
                              <span className="font-semibold text-md text-gray-900">
                                Polygon (
                                {
                                  areaMeasurements.filter(
                                    (m) =>
                                      !saveAreaHistory.some(
                                        (s) => s.id === m.id
                                      ) && m.isImport === false
                                  ).length
                                }
                                )
                              </span>
                            </div>
                          )}

                          <div className="space-y-2">
                            {areaMeasurements
                              .filter(
                                (m) =>
                                  !saveAreaHistory.some((s) => s.id === m.id) &&
                                  m.isImport === false
                              )
                              .map((measurement, index) => (
                                <React.Fragment key={index}>
                                  <div
                                    key={measurement.id}
                                    className={`bg-[#F5F7FA] p-[7px] rounded-[10px] flex items-center gap-1 
                                      border-[1.5px]  bg-[#E9F8FE]`}
                                    style={{
                                      borderColor:
                                        measurement?.color === "#FFFFFF"
                                          ? "#e2e5e8ff"
                                          : measurement?.color || "#FFD700",
                                    }}
                                  >
                                    <div className="w-full bg-white p-[5px] rounded-[10px] justify-between items-center">
                                      <div className="flex-1 w-full flex-col flex bg-white rounded-[10px] justify-between items-start">
                                        <div className="flex w-full flex-col items-start">
                                          <Select
                                            className="select-compact"
                                            style={{ width: "100%" }}
                                            placeholder="Select Tooth"
                                            value={
                                              toothById[measurement.id] ??
                                              undefined
                                            } // undefined => show placeholder
                                            status={
                                              toothErrById[measurement.id]
                                                ? "error"
                                                : undefined
                                            }
                                            optionFilterProp="children"
                                            allowClear
                                            onChange={(val) => {
                                              // val is the selected fdi (or undefined when cleared)
                                              setToothById((prev) => ({
                                                ...prev,
                                                [measurement.id]: val,
                                              }));

                                              const isValid =
                                                !!val &&
                                                String(val).trim().length > 0;
                                              setToothErrById((prev) => ({
                                                ...prev,
                                                [measurement.id]: !isValid,
                                              }));
                                            }}
                                          >
                                            {[
                                              "Upper Right Quadrant",
                                              "Upper Left Quadrant",
                                              "Lower Left Quadrant",
                                              "Lower Right Quadrant",
                                            ].map((quad) => (
                                              <Select.OptGroup
                                                key={quad}
                                                label={quad}
                                              >
                                                {teethOptions
                                                  .filter(
                                                    (tooth) =>
                                                      tooth.quadrant === quad
                                                  )
                                                  .map((t) => (
                                                    <Select.Option
                                                      key={t.fdi}
                                                      value={t.fdi}
                                                    >
                                                      {t.toothName} - {t.fdi}
                                                    </Select.Option>
                                                  ))}
                                              </Select.OptGroup>
                                            ))}
                                          </Select>
                                          {toothErrById[measurement.id] && (
                                            <span className="text-[11px] text-start text-red-500 -mt-1">
                                              Tooth # required
                                            </span>
                                          )}
                                          <Select
                                            className="select-compact"
                                            style={{ width: "100%" }}
                                            placeholder="Select Label"
                                            value={labelById[measurement.id]}
                                            onChange={(val) => {
                                              setLabelById((prev) => ({
                                                ...prev,
                                                [measurement.id]: val,
                                              }));
                                              const isValid =
                                                !!val &&
                                                String(val).trim().length > 0;
                                              setLabelErrById((prev) => ({
                                                ...prev,
                                                [measurement.id]: !isValid,
                                              }));
                                            }}
                                          >
                                            {_teethOptions.map((el, index) => {
                                              return (
                                                <Select.Option
                                                  key={index}
                                                  value={el.value}
                                                >
                                                  {el.title}
                                                </Select.Option>
                                              );
                                            })}
                                          </Select>
                                          {labelErrById[measurement.id] && (
                                            <span className="text-[11px] text-start text-red-500 -mt-1">
                                              Label # required
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex-1 mt-2 flex flex-col bg-white rounded-[10px]">
                                        <span className="text-sm text-[#525866] font-semibold">
                                          Area: {measurement.area.toFixed(2)}{" "}
                                          {scale > 0 ? "mm²" : "px"}
                                        </span>
                                        <span className="text-sm text-[#525866] font-semibold">
                                          Perimeter:{" "}
                                          {measurement.perimeter.toFixed(2)}{" "}
                                          {scale > 0 ? "mm²" : "px"}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      className={`text-center cursor-pointer w-[50px] ${
                                        selectedId === measurement.id
                                          ? "text-[#0A9FD4]"
                                          : "text-[#525866]"
                                      } font-semibold`}
                                      onClick={() =>
                                        saveMeasurementDirect(
                                          measurement,
                                          "area"
                                        )
                                      }
                                    >
                                      Save
                                    </div>
                                  </div>
                                </React.Fragment>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <Activity className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <div className="text-sm font-medium">
                        No active measurements
                      </div>
                      <div className="text-xs mt-1">
                        Use tools to start measuring
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Measurement History - Scrollable */}
              {activeTab === "history" && (
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex-1 min-h-0">
                    <ScrollArea className="h-full">
                      <div className="p-2 pt-4">
                        {sortedSavedMeasurements.length > 0 ? (
                          <div className="space-y-4">
                            {saveLinearHistory.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <img src={dentalIcons.linear} />
                                  <span className="font-semibold text-md text-gray-900">
                                    Linear ({saveLinearHistory.length})
                                  </span>
                                </div>
                                {saveLinearHistory.map((saved) => (
                                  <div
                                    key={saved.id}
                                    style={{
                                      borderColor:
                                        saved?.color === "#FFFFFF"
                                          ? "#e2e5e8ff"
                                          : saved?.color,
                                    }}
                                    className="bg-[#F5F7FA] rounded-[10px] p-2 border-[1.5px]  transition-colors"
                                  >
                                    <div className="flex flex-col items-start justify-between">
                                      <div className="flex items-center gap-2">
                                        <div className="h-3 w-3 bg-[#0A9FD4]" />
                                        <span className="font-medium text-[16px] text-[#535353]">
                                          {
                                            teethOptions.find(
                                              (el) =>
                                                el.fdi === saved.toothNumber
                                            )?.toothName
                                          }{" "}
                                          ({saved.toothNumber})
                                        </span>
                                      </div>
                                      <div className="flex w-full items-center gap-2 justify-between mt-1">
                                        <div>
                                          <span className="text-[14px] font-semibold text-[#000000]">
                                            D = {saved.value}
                                          </span>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            onDeleteSavedMeasurement(saved.id)
                                          }
                                          className="h-4 w-4 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {saveAngleHistory.length > 0 && (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <img src={dentalIcons.angles} />
                                  <span className="font-semibold text-md text-gray-900">
                                    Angle ({saveAngleHistory.length})
                                  </span>
                                </div>
                                {saveAngleHistory.map((saved) => (
                                  <div
                                    key={saved.id}
                                    style={{
                                      borderColor:
                                        saved?.color === "#FFFFFF"
                                          ? "#e2e5e8ff"
                                          : saved?.color,
                                    }}
                                    className="bg-[#F5F7FA] rounded-[10px] p-2 border-[1.5px] transition-colors"
                                  >
                                    <div className="flex flex-col items-start justify-between">
                                      <div className="flex items-center gap-2">
                                        <div className="h-3 w-3 bg-[#0A9FD4]" />
                                        <span className="font-medium text-[16px] text-[#535353]">
                                          {
                                            teethOptions.find(
                                              (el) =>
                                                el.fdi === saved.toothNumber
                                            )?.toothName
                                          }{" "}
                                          ({saved?.toothNumber})
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 justify-between w-full">
                                        <div className="flex flex-col">
                                          <span className="text-[14px] font-semibold text-[#000000]">
                                            Angle = {saved.value}
                                          </span>
                                          <span className="text-[13px] font-semibold text-[#8C8C8C]">
                                            D1 ={" "}
                                            {saved?.arm1Distance?.toFixed(2)}{" "}
                                            {scale > 0 ? "mm" : "px"}
                                          </span>
                                          <span className="text-[13px] font-semibold text-[#8C8C8C]">
                                            D2 ={" "}
                                            {saved?.arm2Distance?.toFixed(2)}{" "}
                                            {scale > 0 ? "mm" : "px"}
                                          </span>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            onDeleteSavedMeasurement(saved.id)
                                          }
                                          className="h-4 w-4 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {saveAreaHistory.length > 0 && (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <img src={dentalIcons.polygons} />
                                  <span className="font-semibold text-md text-gray-900">
                                    Polygon ({saveAreaHistory.length})
                                  </span>
                                </div>
                                {saveAreaHistory.map((saved) => (
                                  <div
                                    key={saved.id}
                                    style={{
                                      borderColor:
                                        saved?.color === "#FFFFFF"
                                          ? "#e2e5e8ff"
                                          : saved?.color,
                                    }}
                                    className="bg-[#F5F7FA] rounded-[10px] p-2 border-[1.5px] transition-colors"
                                  >
                                    <div className="flex flex-col items-start justify-between">
                                      <ToothBadge saved={saved} />

                                      <div className="w-full flex items-center gap-2">
                                        <div
                                          className="h-3 w-3 bg-[#0A9FD4]"
                                          style={{
                                            background:
                                              saved?.color || "#FFD700",
                                          }}
                                        />
                                        <div className="w-full flex items-center justify-between">
                                          <span className="font-medium text-[16px] text-[#535353]">
                                            {
                                              teethOptions.find(
                                                (el) =>
                                                  el.fdi === saved.toothNumber
                                              )?.toothName
                                            }{" "}
                                            ({saved?.toothNumber})
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex w-full items-end justify-end gap-2 mt-1">
                                        <div className="flex flex-col items-start justify-center w-full">
                                          {(() => {
                                            const m = (saved.value || "").match(
                                              /A:\s*([\d.]+)\s*([^\s]+).*P:\s*([\d.]+)\s*([^\s)]+)/
                                            );
                                            return m ? (
                                              <>
                                                <span className="text-[14px] font-semibold text-[#000000]">
                                                  Area: {m[1]} {m[2]}
                                                </span>
                                                <span className="text-[13px] font-semibold text-[#8C8C8C]">
                                                  Perimeter: {m[3]} {m[4]}
                                                </span>
                                              </>
                                            ) : (
                                              <span className="text-[15px] font-semibold text-[#000000]">
                                                {saved.value}
                                              </span>
                                            );
                                          })()}
                                        </div>

                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            onDeleteSavedMeasurement(saved.id)
                                          }
                                          className="h-4 w-4 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                              <Save className="w-6 h-6 text-gray-400" />
                            </div>
                            <div className="text-sm font-medium">
                              No saved measurements
                            </div>
                            <div className="text-xs mt-1">
                              Save active measurements to build history
                            </div>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              )}
            </>
          )}

          {selectedTab === 2 && (
            <div className="image_analysis_container">
              <div className="px-1 py-2 flex items-start gap-2 h-full">
                <img
                  src={dentalIcons.polygonIcon}
                  alt="linear"
                  className="h-6 w-6"
                />
                <p className="font-semibold text-black text-[16px]">
                  Morphometric Analysis
                </p>
              </div>

              <div className="flex flex-col justify-between w-full">
                <div className="p-2 bg-[#DFF3FA] border border-[#E2F7FF] rounded-[12px]">
                  <div className="p-3 bg-white rounded-[12px]">
                    {/* <div className="flex items-start gap-2 mb-2">
                      <InfoIcon className="w-4 h-4 text-[#20C3FF] shrink-0 mt-1" />
                      <p className="mb-0 leading-6 text-black">
                        Compare shapes from previous images with the current one
                        to assess dimensional and morphological differences.
                      </p>
                    </div> */}

                    <Tooltip.Provider delayDuration={150}>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <div className="flex items-start gap-2 mb-2">
                            <button
                              type="button"
                              className="shrink-0 mt-1 leading-none"
                            >
                              <InfoIcon className="w-4 h-4 text-[#20C3FF]" />
                            </button>
                            <p className="mb-0 leading-6 text-black">
                              Import stored shapes to perform shape-to-shape
                              analysis.
                            </p>
                          </div>
                        </Tooltip.Trigger>

                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="left"
                            align="start"
                            sideOffset={8}
                            className="w-64 rounded-md bg-white border border-[#EDEDED] shadow-md px-3 py-2 text-sm text-black z-[9999]"
                          >
                            Compare shapes from previous images with the current
                            one to assess dimensional and morphological
                            differences.
                            <Tooltip.Arrow className="fill-white" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>

                    <CustomPrimaryButton
                      icon={dentalIcons.importPoly}
                      title="Import Reference Shape"
                      isRound={true}
                      leadingIcon
                      width={"100%"}
                      onClick={() => setShowImportPolygonWindow(true)}
                    />
                    {/* <small>
                      Import stored shapes to perform shape-to-shape analysis.
                    </small> */}
                  </div>
                </div>
                {/* <div className="px-1 py-2 flex items-start gap-2 mt-2">
                  <img
                    src={dentalIcons.estimateAiAge}
                    alt="linear"
                    className="h-6 w-6"
                  />
                  <p className="font-semibold text-black text-[16px]">
                    Tooth Ratio Validation
                  </p>
                </div> */}
              </div>
              {/* <div className="p-2 mt-3 bg-[#DFF3FA] border border-[#E2F7FF] rounded-[12px]">
                <div className="p-3 bg-white rounded-[12px]">
                  <div className="flex items-start gap-2 mb-2">
                    <InfoIcon className="w-4 h-4 text-[#20C3FF] shrink-0 mt-1" />
                    <p className="mb-0 leading-6 text-black">
                      Evaluate pulp-to-tooth area to determine validity for age
                      estimation.
                    </p>
                  </div>

                  <CustomPrimaryButton
                    icon={dentalIcons.estimateAge}
                    title="Check Tooth Ratio"
                    isRound={true}
                    leadingIcon
                    width={"100%"}
                    onClick={() => setShowEstimateAge(true)}
                  />
                  <small>
                    Import stored areas to perform pulp-to-tooth analysis.
                  </small>
                </div>
              </div> */}

              <div className="p-2 mt-3 bg-[#DFF3FA] border border-[#E2F7FF] rounded-[12px]">
                <div className="p-3 bg-white rounded-[12px]">
                  {/* <div className="flex items-start gap-2 mb-2">
                    <InfoIcon className="w-4 h-4 text-[#20C3FF] shrink-0 mt-1" />
                    <p className="mb-0 leading-6 text-black">
                      Evaluate pulp-to-tooth area to determine validity for age
                      estimation.
                    </p>
                  </div> */}

                  <Tooltip.Provider delayDuration={150}>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <div className="flex items-start gap-2 mb-2">
                          <button
                            type="button"
                            className="shrink-0 mt-1 leading-none"
                          >
                            <InfoIcon className="w-4 h-4 text-[#20C3FF]" />
                          </button>
                          <p className="mb-0 leading-6 text-black">
                            Import stored areas to perform pulp-to-tooth
                            analysis.
                          </p>
                        </div>
                      </Tooltip.Trigger>

                      <Tooltip.Portal>
                        <Tooltip.Content
                          side="left"
                          align="start"
                          sideOffset={8}
                          className="w-64 rounded-md bg-white border border-[#EDEDED] shadow-md px-3 py-2 text-sm text-black z-[9999]"
                        >
                          Evaluate pulp-to-tooth area to determine validity for
                          age estimation.
                          <Tooltip.Arrow className="fill-white" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>

                  <CustomPrimaryButton
                    icon={dentalIcons.estimateAge}
                    title="Check Tooth Ratio"
                    isRound={true}
                    leadingIcon
                    width={"100%"}
                    onClick={() => setShowEstimateAge(true)}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <CustomModal
        open={showImportPolygonWindow}
        onCancel={() => setShowImportPolygonWindow(false)}
        width={"60%"}
        className={"no-pad-modal"}
      >
        <div className="h-[650px] image_analysis_container">
          {/* header */}
          <div className="border-b border-[#E1E4EA] h-[70px] w-full flex items-center gap-2 bg-white px-4">
            <img src={dentalIcons.importPolygon} alt="import polygon" />
            <h4>Morphometric Analysis</h4>
          </div>
          {/* body */}
          <div className="h-[510px] w-[100%] flex">
            <div className="w-[35%] h-full bg-[#F5F7FA] p-4">
              <div className="bg-[#FFFFFF] border border-[#F2F4F5] rounded-[8px] p-1 flex items-center">
                <img src={dentalIcons.searchIcon} alt="search" />
                <Input
                  placeholder="Search"
                  className="pe-input-no-border"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="mt-2 mb-2">
                <p className="text-[#717784] font-semibold text-md">
                  Select a project from the list to load available polygons.
                </p>
              </div>
              <div className="space-y-2 h-[390px] overflow-scroll pb-2">
                {projectList.length > 0 ? (
                  projectList
                    .filter((p) => p.id !== Number(projectId))
                    .map((el, index) => {
                      return (
                        <div
                          onClick={() => handleSelectProject(el)}
                          key={index}
                          className={`flex items-center justify-between gap-2 ${
                            selectedProjectId === el.id
                              ? "bg-white shadow-md"
                              : "bg-transparent"
                          } hover:bg-white p-2 rounded-[8px]  cursor-pointer`}
                        >
                          <div className="flex items-start gap-2">
                            <div>
                              <img
                                src={dentalIcons.projectAnalysisIcon}
                                alt="project"
                                className="h-10 w-10"
                              />
                            </div>
                            <div>
                              <div className="rounded-[32px]  inline-flex justify-center items-center gap-1 overflow-hidden">
                                <Folder
                                  className="h-3 w-3 text-[#2563EB]"
                                  strokeWidth={1.8}
                                  color="#20C3FF"
                                />

                                <div className="text-cyan-400 text-sm font-medium">
                                  #{el.identifier}
                                </div>
                              </div>
                              <p className="text-[#525866] font-medium text-[14px]">
                                {el.title}
                              </p>
                              <span className="text-[#8F8F8F] font-normal text-[12px]">
                                {formateDateAndTime(
                                  el.createAt,
                                  "ddd MM, YYYY"
                                )}
                              </span>
                            </div>
                          </div>
                          <div>
                            <img
                              src={dentalIcons.chewronLeft}
                              alt="arrow"
                              className="h-6 w-6"
                            />
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="h-full flex items-center justify-center w-full">
                    No Project found
                  </div>
                )}
              </div>
            </div>
            {polygonsLoading ? (
              <div className="w-[65%] h-full p-3 space-y-3 overflow-scroll">
                <PolygonsShimer />
              </div>
            ) : (
              <div className="w-[65%] h-full p-3 space-y-3 overflow-scroll">
                {measurementsForComparison.length > 0 && (
                  <div className="flex items-center gap-2">
                    <img src={dentalIcons.angleTool} />
                    <p className="text-[#000000] text-[14px] font-medium">
                      Select Polygon
                    </p>
                  </div>
                )}
                {measurementsForComparison.length > 0 ? (
                  measurementsForComparison
                    .filter((m) => m.type === "area")
                    .map((el, index) => {
                      const isSelected = selectedPolygons.some(
                        (p) => p.id === el.id
                      );

                      const m = (el.value || "").match(
                        /A:\s*([\d.]+)\s*([^\s]+).*P:\s*([\d.]+)\s*([^\s)]+)/
                      );
                      return (
                        <div
                          onClick={() => {
                            setSelectdPolygonId(el.id);
                            handleTogglePolygon(el);
                          }}
                          key={index}
                          className={`${
                            isSelected
                              ? "border-2 border-[#20C3FF] bg-[#20C3FF1A]"
                              : "border-1 border-[#E1E4EA] bg-[#FFFFFF]"
                          }  rounded-[8px] p-2 space-y-3  cursor-pointer`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-[#535353] font-semibold text-[16px]">
                                {
                                  teethOptions.find(
                                    (t) => t.fdi === el.toothNumber
                                  )?.toothName
                                }{" "}
                                - (
                                {
                                  teethOptions.find(
                                    (t) => t.fdi === el.toothNumber
                                  )?.fdi
                                }
                                )
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <ToothBadge saved={el} />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pb-1">
                            <div className="bg-[#F8F8F9] rounded-full border border-[#E8E9EB] px-2 flex items-center gap-2 text-[#5B5E65] font-medium">
                              <img src={dentalIcons.polyArea} alt="area" />
                              <p>
                                Area: {m[1]} {m[2]}
                              </p>
                            </div>
                            <div className="bg-[#F8F8F9] rounded-full border border-[#E8E9EB] px-2 flex items-center gap-2 text-[#5B5E65] font-medium">
                              <img
                                src={dentalIcons.polyPrimter}
                                alt="perimeter"
                              />
                              <p>
                                Perimeter: {m[3]} {m[4]}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="w-[100%] h-full flex flex-col items-center justify-center font-semibold text-[#5B5E65]">
                    <img src={dentalIcons.polygons} className="h-10" />
                    {selectedProjectId === null
                      ? "Choose a project from the left to view its saved polygons."
                      : "No polygons found in this project."}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* footer */}
          <div className="border-t border-[#E1E4EA] h-[70px] w-full flex items-center justify-between gap-2 bg-white px-4">
            <CustomButtonWithIcon
              title="Cancel"
              isRound
              onClick={() => setShowImportPolygonWindow(false)}
            />
            <CustomPrimaryButton
              title="Import"
              isRound
              isDisbale={selectedPolygons.length === 0}
              onClick={onImportPolygon}
            />
          </div>
        </div>
      </CustomModal>{" "}
    </div>
  );
}
